// ===========================================================================
// Rendering Helpers
// Canvas rendering utilities for the simulation
// ===========================================================================

import {
  WORLD_WIDTH,
  WORLD_HEIGHT,
  Track,
  Detection,
  CrowdRegion,
  AgentPerson,
  AgentWeapon,
  WEAPON_SIZE,
} from '../types';

// ----------------------------- Grid Rendering ------------------------------

/**
 * Render a numeric grid to a canvas with color mapping
 */
export function renderGridToCanvas(
  grid: number[][],
  canvas: HTMLCanvasElement,
  colorMap: 'viridis' | 'heat' | 'grayscale' = 'viridis',
  normalize: boolean = true
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  if (rows === 0 || cols === 0) return;

  // Find min/max for normalization
  let minVal = Infinity;
  let maxVal = -Infinity;

  if (normalize) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        minVal = Math.min(minVal, grid[row][col]);
        maxVal = Math.max(maxVal, grid[row][col]);
      }
    }
  } else {
    minVal = 0;
    maxVal = 1;
  }

  const range = maxVal - minVal || 1;

  // Create image data
  const imageData = ctx.createImageData(cols, rows);

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const value = (grid[row][col] - minVal) / range;
      const [r, g, b] = getColorForValue(value, colorMap);

      const idx = (row * cols + col) * 4;
      imageData.data[idx] = r;
      imageData.data[idx + 1] = g;
      imageData.data[idx + 2] = b;
      imageData.data[idx + 3] = 255;
    }
  }

  // Scale to canvas size
  canvas.width = cols;
  canvas.height = rows;
  ctx.putImageData(imageData, 0, 0);
}

/**
 * Get RGB color for a normalized value [0, 1]
 */
function getColorForValue(
  value: number,
  colorMap: 'viridis' | 'heat' | 'grayscale'
): [number, number, number] {
  const v = Math.max(0, Math.min(1, value));

  switch (colorMap) {
    case 'viridis':
      return viridisColor(v);
    case 'heat':
      return heatColor(v);
    case 'grayscale':
    default:
      const g = Math.floor(v * 255);
      return [g, g, g];
  }
}

/**
 * Viridis-like colormap
 */
function viridisColor(t: number): [number, number, number] {
  // Simplified viridis approximation
  const r = Math.floor(Math.max(0, Math.min(255, (1.5 - Math.abs(t - 0.75) * 4) * 255 * t)));
  const g = Math.floor(Math.max(0, Math.min(255, Math.sin(t * Math.PI) * 255)));
  const b = Math.floor(Math.max(0, Math.min(255, (1 - t) * 200 + 55)));
  return [r, g, b];
}

/**
 * Heat colormap (black -> red -> yellow -> white)
 */
function heatColor(t: number): [number, number, number] {
  let r: number, g: number, b: number;

  if (t < 0.33) {
    r = t * 3 * 255;
    g = 0;
    b = 0;
  } else if (t < 0.66) {
    r = 255;
    g = (t - 0.33) * 3 * 255;
    b = 0;
  } else {
    r = 255;
    g = 255;
    b = (t - 0.66) * 3 * 255;
  }

  return [Math.floor(r), Math.floor(g), Math.floor(b)];
}

// ----------------------------- Overlay Rendering ---------------------------

/**
 * Render grid as semi-transparent overlay on main canvas
 */
export function renderGridOverlay(
  ctx: CanvasRenderingContext2D,
  grid: number[][],
  colorMap: 'viridis' | 'heat' | 'grayscale',
  alpha: number = 0.5,
  canvasWidth: number,
  canvasHeight: number
): void {
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  if (rows === 0 || cols === 0) return;

  // Find max for normalization
  let maxVal = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      maxVal = Math.max(maxVal, grid[row][col]);
    }
  }
  if (maxVal === 0) return;

  const cellW = canvasWidth / cols;
  const cellH = canvasHeight / rows;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const value = grid[row][col] / maxVal;
      if (value < 0.05) continue; // Skip very low values

      const [r, g, b] = getColorForValue(value, colorMap);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha * value})`;
      ctx.fillRect(col * cellW, row * cellH, cellW, cellH);
    }
  }
}

// ----------------------------- Agent Rendering -----------------------------

/**
 * Draw a person agent
 */
export function drawPerson(
  ctx: CanvasRenderingContext2D,
  person: AgentPerson,
  isSuspect: boolean,
  isSelected: boolean,
  scale: number
): void {
  const x = person.x * scale;
  const y = person.y * scale;
  const r = person.radius * scale;

  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);

  // Fill color based on state
  if (isSuspect) {
    ctx.fillStyle = 'rgba(239, 68, 68, 0.8)'; // Red for suspect
  } else {
    ctx.fillStyle = 'rgba(59, 130, 246, 0.7)'; // Blue for normal
  }
  ctx.fill();

  // Stroke
  ctx.strokeStyle = isSelected ? '#ffffff' : isSuspect ? '#ff0000' : '#3b82f6';
  ctx.lineWidth = isSelected ? 3 : 1.5;
  ctx.stroke();

  // Head indicator
  ctx.beginPath();
  ctx.arc(x, y - r * 0.4, r * 0.35, 0, Math.PI * 2);
  ctx.fillStyle = isSuspect ? 'rgba(200, 50, 50, 0.9)' : 'rgba(80, 150, 255, 0.9)';
  ctx.fill();
}

/**
 * Draw a weapon marker
 */
export function drawWeapon(
  ctx: CanvasRenderingContext2D,
  weapon: AgentWeapon,
  isSelected: boolean,
  scale: number
): void {
  const x = weapon.x * scale;
  const y = weapon.y * scale;
  const size = WEAPON_SIZE * scale;

  // Draw warning triangle
  ctx.beginPath();
  ctx.moveTo(x, y - size);
  ctx.lineTo(x - size * 0.866, y + size * 0.5);
  ctx.lineTo(x + size * 0.866, y + size * 0.5);
  ctx.closePath();

  ctx.fillStyle = 'rgba(168, 85, 247, 0.8)';
  ctx.fill();
  ctx.strokeStyle = isSelected ? '#ffffff' : '#a855f7';
  ctx.lineWidth = isSelected ? 3 : 2;
  ctx.stroke();

  // Exclamation mark
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${size}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('!', x, y);
}

// ----------------------------- Detection/Track Rendering -------------------

/**
 * Draw bounding box for a detection
 */
export function drawBoundingBox(
  ctx: CanvasRenderingContext2D,
  det: Detection,
  scale: number,
  showConf: boolean = true
): void {
  const x1 = det.x1 * scale;
  const y1 = det.y1 * scale;
  const w = (det.x2 - det.x1) * scale;
  const h = (det.y2 - det.y1) * scale;

  ctx.strokeStyle = det.cls === 'weapon' ? '#a855f7' : '#22c55e';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 4]);
  ctx.strokeRect(x1, y1, w, h);
  ctx.setLineDash([]);

  if (showConf) {
    ctx.fillStyle = det.cls === 'weapon' ? '#a855f7' : '#22c55e';
    ctx.font = '10px monospace';
    ctx.fillText(`${det.cls} ${(det.conf * 100).toFixed(0)}%`, x1, y1 - 3);
  }
}

/**
 * Draw track ID label
 */
export function drawTrackId(
  ctx: CanvasRenderingContext2D,
  track: Track,
  scale: number
): void {
  const x = track.cx * scale;
  const y = track.y1 * scale - 5;

  ctx.fillStyle = track.label === 'suspect' ? '#ef4444' : '#3b82f6';
  ctx.font = 'bold 11px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`#${track.trackId}`, x, y);
}

/**
 * Draw track history trail
 */
export function drawTrackHistory(
  ctx: CanvasRenderingContext2D,
  track: Track,
  scale: number
): void {
  if (track.history.length < 2) return;

  ctx.beginPath();
  ctx.moveTo(track.history[0].x * scale, track.history[0].y * scale);

  for (let i = 1; i < track.history.length; i++) {
    ctx.lineTo(track.history[i].x * scale, track.history[i].y * scale);
  }

  ctx.strokeStyle = track.label === 'suspect' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(59, 130, 246, 0.4)';
  ctx.lineWidth = 2;
  ctx.stroke();
}

// ----------------------------- Contour Rendering ---------------------------

/**
 * Draw a crowd region contour
 */
export function drawContour(
  ctx: CanvasRenderingContext2D,
  region: CrowdRegion,
  scale: number
): void {
  if (region.contour.length < 3) return;

  ctx.beginPath();
  ctx.moveTo(region.contour[0][0] * scale, region.contour[0][1] * scale);

  for (let i = 1; i < region.contour.length; i++) {
    ctx.lineTo(region.contour[i][0] * scale, region.contour[i][1] * scale);
  }
  ctx.closePath();

  // Fill based on alert level
  if (region.mob) {
    ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
    ctx.strokeStyle = '#ef4444';
  } else if (region.insideCount > 0) {
    ctx.fillStyle = 'rgba(234, 179, 8, 0.2)';
    ctx.strokeStyle = '#eab308';
  } else {
    ctx.fillStyle = 'rgba(34, 197, 94, 0.1)';
    ctx.strokeStyle = '#22c55e';
  }

  ctx.fill();
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw label
  ctx.fillStyle = region.mob ? '#ef4444' : '#eab308';
  ctx.font = 'bold 12px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(
    region.mob ? 'MOB' : `${region.insideCount}`,
    region.centroid.x * scale,
    region.centroid.y * scale
  );
}

// ----------------------------- Alert Banners -------------------------------

/**
 * Draw alert banner at top of canvas
 */
export function drawAlertBanner(
  ctx: CanvasRenderingContext2D,
  type: 'crowd' | 'mob' | 'weapon',
  canvasWidth: number
): void {
  const y = type === 'mob' ? 5 : type === 'crowd' ? 35 : 65;
  const height = 28;

  let text: string;
  let bgColor: string;
  let textColor: string;

  switch (type) {
    case 'mob':
      text = '⚠ MOB ALERT - IMMEDIATE THREAT';
      bgColor = 'rgba(239, 68, 68, 0.9)';
      textColor = '#ffffff';
      break;
    case 'crowd':
      text = '🔶 CROWD DETECTED';
      bgColor = 'rgba(234, 179, 8, 0.85)';
      textColor = '#000000';
      break;
    case 'weapon':
      text = '⚡ WEAPON DETECTED';
      bgColor = 'rgba(168, 85, 247, 0.85)';
      textColor = '#ffffff';
      break;
  }

  ctx.fillStyle = bgColor;
  ctx.fillRect(10, y, canvasWidth - 20, height);

  ctx.fillStyle = textColor;
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvasWidth / 2, y + height / 2);
}

// ----------------------------- Coordinate Transforms -----------------------

/**
 * Convert world coordinates to canvas coordinates
 */
export function worldToCanvas(
  worldX: number,
  worldY: number,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number } {
  return {
    x: (worldX / WORLD_WIDTH) * canvasWidth,
    y: (worldY / WORLD_HEIGHT) * canvasHeight,
  };
}

/**
 * Convert canvas coordinates to world coordinates
 */
export function canvasToWorld(
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number } {
  return {
    x: (canvasX / canvasWidth) * WORLD_WIDTH,
    y: (canvasY / canvasHeight) * WORLD_HEIGHT,
  };
}

/**
 * Get scale factor from world to canvas
 */
export function getScale(canvasWidth: number, canvasHeight: number): number {
  // Use minimum to maintain aspect ratio
  return Math.min(canvasWidth / WORLD_WIDTH, canvasHeight / WORLD_HEIGHT);
}
